<template>
  <div class="transactions-table">
    <app-loading :loading="loading" />
    <template v-if="!loading">
      <!-- Filters -->
      <recognition-filters
        :programs="programs"
        :program-id="programId"
        :selected-program="selectedProgram"
        :selected-date-range="selectedDateRange"
        :date-range-options="dateRangeOptions"
        :has-active-filters="hasActiveFilters"
        @update:selected-program="selectedProgram = $event"
        @update:selected-date-range="selectedDateRange = $event"
        @reset="resetFilters"
      >
        <template #prepend>
          <b-form-select v-model="selectedCategory" :options="categoryOptions" class="recognition-filters__filter" />
        </template>
        <template #append>
          <export-button
            file-name="Recognition_Transactions"
            :export-headers="exportHeaders"
            :export-data="transactions"
            :export-file-rows="exportFileRow"
            :disabled="transactions.length === 0"
            :enable-pdf="false"
            display-text="Export as"
          />
        </template>
      </recognition-filters>

      <b-table
        v-if="transactions.length > 0"
        :items="transactions"
        :fields="TRANSACTIONS_TABLE_FIELDS"
        hover
        responsive
        borderless
        show-empty
        class="transactions-table__table"
      >
        <template #cell(datetime_of_transaction)="{ item }">
          <span>{{ formatDate(item.datetime_of_transaction) }}</span>
        </template>

        <template #cell(category)="{ item }">
          <span>{{ formatCategory(item.category) }}</span>
        </template>

        <template #cell(program_name)="{ item }">
          <span>{{ item.program_name || '-' }}</span>
        </template>

        <template #cell(granter_name)="{ item }">
          <div class="transactions-table__person">
            <span>{{ item.granter_name || '-' }}</span>
            <span v-if="item.granter_employee_code" class="transactions-table__person-code">
              [{{ item.granter_employee_code }}]
            </span>
          </div>
        </template>

        <template #cell(nominee_name)="{ item }">
          <div class="transactions-table__person">
            <span>{{ item.nominee_name || '-' }}</span>
            <span v-if="item.employee_code" class="transactions-table__person-code"> [{{ item.employee_code }}] </span>
          </div>
        </template>

        <template #cell(redemption_details)="{ item }">
          <div class="transactions-table__redemption-details">
            <template
              v-if="item.category === 'AWARD_GRANTED' && item.valid_grant_types && item.valid_grant_types.length > 0"
            >
              <div class="transactions-table__redemption-label">Redeemable as:</div>
              <div v-for="grantType in item.valid_grant_types" :key="grantType">
                {{ formatGrantType(grantType) }}
              </div>
            </template>
            <template v-else>
              {{ formatRedemptionDetails(item) }}
            </template>
          </div>
        </template>

        <template #cell(amount_minor)="{ item }">
          <span :class="getAmountClass(item)">
            {{ formatAmount(item) }}
          </span>
        </template>

        <template #cell(balance_after_minor)="{ item }">
          <span>{{ formatCurrency(item.balance_after_minor) }}</span>
        </template>
      </b-table>

      <div v-else class="transactions-table__empty">
        <p class="text-muted">No transactions found.</p>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import moment from 'moment';
import AppLoading from '@/components/AppLoading.vue';
import ExportButton from '@/components/ExportButton.vue';
import RecognitionFilters from '@/pages/recognition/components/RecognitionFilters.vue';
import { toCurrency } from '@/utils';
import { getDateFromForRange } from '@/pages/recognition/constants';
import ApiClient from '@/ApiClient';
import State from '@/state/State';

const props = defineProps({
  programs: {
    type: Array,
    default: () => [],
  },
  programId: {
    type: String,
    default: null,
  },
});

const transactions = ref([]);
const loading = ref(false);
const fetchRequestId = ref(0);

const selectedCategory = ref(null);
const selectedProgram = ref(null);
const selectedDateRange = ref('last_30_days');

const TRANSACTIONS_TABLE_FIELDS = [
  {
    key: 'datetime_of_transaction',
    label: 'Date',
  },
  {
    key: 'category',
    label: 'Event',
  },
  {
    key: 'program_name',
    label: 'Award',
  },
  {
    key: 'granter_name',
    label: 'From',
  },
  {
    key: 'nominee_name',
    label: 'To',
  },
  {
    key: 'redemption_details',
    label: 'Redemption details',
  },
  {
    key: 'amount_minor',
    label: 'Amount',
  },
  {
    key: 'balance_after_minor',
    label: 'Balance',
  },
];

const categoryOptions = [
  { value: null, text: 'Event' },
  { value: 'DEPOSIT', text: 'Deposit' },
  { value: 'AWARD_GRANTED', text: 'Award granted' },
  { value: 'REWARD_EXPIRED', text: 'Reward expired' },
  { value: 'AWARD_REDEEMED', text: 'Reward redeemed' },
  { value: 'AWARD_WITHDRAWN', text: 'Award withdrawn' },
];

const dateRangeOptions = [
  { value: 'last_30_days', text: 'Last 30 days' },
  { value: 'last_90_days', text: 'Last 90 days' },
  { value: 'last_year', text: 'Last year' },
  { value: null, text: 'All time' },
];

const currency = computed(() => State.state?.company?.default_currency);

const hasActiveFilters = computed(() => {
  const hasProgramFilter = !props.programId && selectedProgram.value !== null;
  return selectedCategory.value !== null || hasProgramFilter || selectedDateRange.value !== 'last_30_days';
});

const fetchTransactions = async () => {
  fetchRequestId.value += 1;
  const currentRequestId = fetchRequestId.value;

  loading.value = true;
  try {
    const params = {};
    if (selectedCategory.value) {
      params.category = selectedCategory.value;
    }
    if (props.programId) {
      params.recognition_award_program_id = props.programId;
    } else if (selectedProgram.value) {
      params.recognition_award_program_id = selectedProgram.value;
    }
    const dateFrom = getDateFromForRange(selectedDateRange.value);
    if (dateFrom) {
      params.date_from = dateFrom;
    }
    const result = await ApiClient.getLedgerTransactions(params);

    if (currentRequestId !== fetchRequestId.value) {
      return;
    }

    transactions.value = (result.transactions || []).map((txn, index) => ({
      ...txn,
      _uid: `${txn.datetime_of_transaction}-${txn.category}-${index}`,
    }));
  } catch (e) {
    if (currentRequestId !== fetchRequestId.value) {
      return;
    }
    transactions.value = [];
  } finally {
    if (currentRequestId === fetchRequestId.value) {
      loading.value = false;
    }
  }
};

const resetFilters = () => {
  selectedCategory.value = null;
  if (!props.programId) {
    selectedProgram.value = null;
  }
  selectedDateRange.value = 'last_30_days';
};

const formatDate = dateStr => {
  if (!dateStr) {
    return '-';
  }
  return moment(dateStr).format('D MMM YYYY');
};

const formatCategory = category => {
  const categoryMap = {
    DEPOSIT: 'Deposit',
    AWARD_GRANTED: 'Award granted',
    REWARD_EXPIRED: 'Reward expired',
    AWARD_REDEEMED: 'Reward redeemed',
    AWARD_WITHDRAWN: 'Award withdrawn',
  };
  return categoryMap[category] || category;
};

const formatCurrency = value => {
  if (value === null || value === undefined) {
    return '-';
  }
  return toCurrency(value, currency.value);
};

const formatRedemptionDetails = item => {
  if (item.category === 'AWARD_REDEEMED') {
    return item.grant_type ? `Redeemed as ${formatGrantType(item.grant_type)}` : 'Redeemed';
  }
  if (item.category === 'AWARD_GRANTED' && item.valid_grant_types?.length > 0) {
    return `Redeemable as ${item.valid_grant_types.map(formatGrantType).join(', ')}`;
  }
  return '-';
};

const formatGrantType = grantType => {
  const grantTypeMap = {
    VOUCHER: 'Gift card',
    BANK_ACCOUNT: 'Cash transferred to bank account',
    WPS: 'Savings pot',
  };
  return grantTypeMap[grantType] || 'Other';
};

const isCreditCategory = category => ['DEPOSIT', 'REWARD_EXPIRED', 'AWARD_WITHDRAWN'].includes(category);

const getAmountClass = item => {
  if (isCreditCategory(item.category)) {
    return 'transactions-table__amount--positive';
  }
  if (item.category === 'AWARD_REDEEMED') {
    return 'transactions-table__amount--neutral';
  }
  return '';
};

const formatAmount = item => {
  const value = item.amount_minor;
  if (value === null || value === undefined) {
    return '-';
  }
  const formatted = toCurrency(value, currency.value);
  if (isCreditCategory(item.category)) {
    return `+${formatted}`;
  }
  if (item.category === 'AWARD_REDEEMED') {
    return formatted;
  }
  return `-${formatted}`;
};

const toExportNumber = value => {
  if (value === null || value === undefined) {
    return '';
  }
  return (value / 100).toFixed(2);
};

const exportAmount = item => {
  const formatted = toExportNumber(item.amount_minor);
  if (formatted === '') {
    return '';
  }
  if (isCreditCategory(item.category) || item.category === 'AWARD_REDEEMED') {
    return formatted;
  }
  return `-${formatted}`;
};

const exportHeaders = [
  'Date',
  'Event',
  'Award',
  'From',
  'From employee no',
  'To',
  'To employee no',
  'Redemption details',
  'Amount',
  'Balance',
];

const exportFileRow = item => {
  return [
    formatDate(item.datetime_of_transaction),
    formatCategory(item.category),
    item.program_name || '-',
    item.granter_name || '-',
    item.granter_employee_code || '-',
    item.nominee_name || '-',
    item.employee_code || '-',
    formatRedemptionDetails(item),
    exportAmount(item),
    toExportNumber(item.balance_after_minor),
  ];
};

// Watch filters and refetch
watch([selectedCategory, selectedProgram, selectedDateRange], () => {
  fetchTransactions();
});

onMounted(() => {
  fetchTransactions();
});

// The parent re-fetches when balance-affecting actions happen elsewhere
// (e.g. an award is withdrawn from the activity tab).
defineExpose({ refresh: fetchTransactions });
</script>

<style scoped lang="scss">
.transactions-table {
  &__table {
    :deep(thead th) {
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--palette-color-default-lighten-40);
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
      padding: 0.75rem 1rem;
    }

    :deep(tbody td) {
      padding: 1rem;
      vertical-align: middle;
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
    }

    :deep(tbody tr:hover) {
      background-color: var(--palette-color-default-lighten-95);
    }
  }

  &__person {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  &__person-code {
    font-size: 0.75rem;
    color: var(--palette-color-default-lighten-40);
  }

  &__redemption-details {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  &__redemption-label {
    font-weight: 500;
  }

  &__amount--positive {
    color: var(--palette-color-success);
  }

  &__amount--neutral {
    color: var(--palette-color-default-lighten-20);
  }

  &__empty {
    padding: 3rem;
    text-align: center;
  }
}
</style>
